import { isTabletPortraitDown, ProviderUser, Spinner, Toast, BaseButton, isDemoModeActive, toggleDemoMode } from '@enter/core';
import { mdiArrowLeft } from '@mdi/js';
import { Icon } from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RootStore } from 'stores';
import { Company, Email, Name, Phone } from '.';
import { SubCategoryLink } from '../components';
import { switchForRoutes } from '../shared';
import * as S from '../shared.styles';

const OverviewComponent: React.FunctionComponent<{ rootStore?: RootStore }> = (props): React.ReactElement => {
            const { rootStore } = props;
            
            const [message, setMessage] = useState<string | undefined>(undefined);

            // The RESOLVED state, not the stored flag (NS-14). A switch whose only job is
            // preventing PHI exposure may not report an intention as a fact.
            const demoActive = isDemoModeActive();
            
            if (!rootStore) {
                return <></>;
            }
            
            // Direct observable access - this MUST trigger re-render when changed
            const users = rootStore.settingsStore.users;
            const roles = rootStore.settingsStore.roles;
            const userId = rootStore.userId;
            const hasAdminAccess = rootStore.roleId === 'PROVIDER_ADMIN_ACCESS';

            const usersLoaded = rootStore.settingsStore.usersLoaded;
            const rolesLoaded = rootStore.settingsStore.rolesLoaded;
            
            // In demo mode these listings are REFUSED at the boundary, so they never "load" and
            // this spinner would own the page forever — taking the Demo Mode section, and the only
            // way back out, with it.
            if ((!usersLoaded || (hasAdminAccess && !rolesLoaded)) && !demoActive) {
                return <Spinner />;
            }
            
            let currentUser = rootStore.settingsStore.getCurrentUser(userId);

            //This is backup in case the current user is not found in the users list
            if(!currentUser) {
                currentUser = rootStore.getProviderUserFromAccount();
            }

            if (!currentUser && !demoActive) {
                return <Spinner />;
            }

            const name = `${currentUser?.first || ''} ${currentUser?.last || ''}`;
            const email = currentUser?.email || '';
            const phone = currentUser?.phone || '';
            const company = rootStore.companyName || '';
            // ENTERING demo mode is staff-only. This allowlist is the access control, and it
            // stays exactly as strict as it was — an earlier version began `isDemoModeActive() ||`,
            // which let any browser with the flag set see the control regardless of who was signed
            // in, opting straight out of the check beside it.
            const canEnableDemoMode =
                email.endsWith('@enter.health') ||
                email.endsWith('@teammwf.com') ||
                email.endsWith('@sage.health');

            // LEAVING it is a different question, and the answer is always yes.
            //
            // `email` comes from the user listing, which demo mode refuses — so once demo mode was
            // on, `email` was '', the allowlist said no, and the "Disable Demo Mode" button
            // vanished. The only way back was clearing localStorage by hand. Jordan hit this on
            // 2026-09-10.
            //
            // Gating the exit on the same condition as the entry reads as symmetry and is not:
            // a switch whose whole purpose is preventing exposure must never be able to strand
            // someone inside it (NS-14).
            const shouldShowDemoMode = canEnableDemoMode || demoActive;
            
            const handleMessageClose = (): void => {
                setMessage(undefined);
            };

            const renderMobile = (): React.ReactElement => (
                <Routes>
                    <Route path="/" element={renderLeftColumn()} />
                    <Route path="name" element={<Name />} />
                    <Route path="email" element={<Email />} />
                    <Route path="phone" element={<Phone />} />
                    <Route path="company" element={<Company />} />
                </Routes>
            );

            const renderLeftColumn = (): React.ReactElement => (
                <S.SubCategoryCol>
                    <S.SubCategoryHeader>
                        {isTabletPortraitDown() && (
                            <S.ReturnLink to="/settings">
                                {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                            </S.ReturnLink>
                        )}
                        <S.SubCategoryH1>Overview</S.SubCategoryH1>
                    </S.SubCategoryHeader>
                    <SubCategoryLink
                        first={true}
                        to="/settings/overview/name"
                        label="Name"
                        value={name}
                    />
                    <SubCategoryLink to="/settings/overview/email" label="Email" value={email} />
                    <SubCategoryLink to="/settings/overview/phone" label="Phone" value={phone} />
                    <SubCategoryLink to="/settings/overview/company" label="Company" value={company} />
                    {shouldShowDemoMode && (
                        <S.DemoModeSection>
                            <S.DemoModeHeader>Demo Mode</S.DemoModeHeader>
                            <S.DemoModeStatus>
                                Status: <strong>{demoActive ? 'Enabled' : 'Disabled'}</strong>
                            </S.DemoModeStatus>
                            <BaseButton 
                                type="button" 
                                variant={demoActive ? 'warning' : 'primary'}
                                onClick={toggleDemoMode}
                            >
                                {demoActive ? 'Disable Demo Mode' : 'Enable Demo Mode'}
                            </BaseButton>
                            <S.DemoModeNote>
                                {demoActive
                                    ? 'Showing generated data. Real requests are blocked and cached data was cleared — a screen with no demo data will show an error rather than real records.'
                                    : 'Show generated data instead of this account. Clears cached data in this browser and blocks requests while it is on.'}
                            </S.DemoModeNote>
                        </S.DemoModeSection>
                    )}
                    <Toast message={message as string} onClose={handleMessageClose} />
                </S.SubCategoryCol>
            );

            const renderRightColumn = (): React.ReactElement => (
                <S.SubCategoryCol>
                    {switchForRoutes([
                        { path: 'name', Component: Name },
                        { path: 'email', Component: Email },
                        { path: 'phone', Component: Phone },
                        { path: 'company', Component: Company },
                    ])}
                </S.SubCategoryCol>
            );
            
            return (
                <S.SubCategoryRow>
                    {isTabletPortraitDown() && renderMobile()}
                    {!isTabletPortraitDown() && renderLeftColumn()}
                    {!isTabletPortraitDown() && renderRightColumn()}
                </S.SubCategoryRow>
            );
};

export const Overview = inject('rootStore')(observer(OverviewComponent));
